/**
 * SimilarPlayerRowContainer - Logic container for player row
 *
 * Wraps the onAddPress callback with stopPropagation and binds item data.
 * @remarks This is a Container component - handles event wrapping
 */

import { memo, useCallback } from "react";
import { GestureResponderEvent } from "react-native";

import SimilarPlayerRowView from "./SimilarPlayerRowView";

import {
  SimilarPlayersPlayerResultFragment,
  SortField,
} from "@/generated/graphql";

/**
 * Props for SimilarPlayerRowContainer component
 */
interface SimilarPlayerRowContainerProps {
  /** Whether the viewport is a mobile screen */
  readonly isMobile: boolean;
  /** The similar player item (used for callbacks) */
  readonly item: SimilarPlayersPlayerResultFragment;
  /** Player's unique identifier */
  readonly playerId: string;
  /** Player's display name */
  readonly playerName: string;
  /** Formatted subtitle text (positions · club) */
  readonly subtitleText: string;
  /** Score value to display in badge, or null when the metric is unavailable */
  readonly score: number | null;
  /** Badge type for score display */
  readonly badgeType: SortField;
  /** Minimum value for badge color scale */
  readonly badgeMin: number;
  /** Maximum value for badge color scale */
  readonly badgeMax: number;
  /** Whether higher values are better for badge color coding */
  readonly badgeIsHigherBetter: boolean;
  /** Whether to use neutral styling instead of color-coded styling */
  readonly badgeIsDefaultStyle?: boolean;
  /** Whether player is already on user's watchlist */
  readonly isOnWatchlist: boolean;
  /** Callback when add-to-list button is pressed with item data */
  readonly onAddPress: (item: SimilarPlayersPlayerResultFragment) => void;
  /** Callback when row is pressed with player ID and name */
  readonly onRowPress: (playerId: string, playerName?: string) => void;
}

/**
 * Container component that wraps event handlers for player row
 * @param props - Container props
 * @param props.item - The similar player item for callback data
 * @param props.playerId - Player ID for navigation
 * @param props.onAddPress - Add press callback (receives item)
 * @param props.onRowPress - Row press callback (receives playerId)
 * @returns SimilarPlayerRowView with wrapped callbacks
 */
const SimilarPlayerRowContainer = ({
  item,
  playerId,
  onAddPress,
  onRowPress,
  ...restProps
}: SimilarPlayerRowContainerProps) => {
  const handleAddPress = useCallback(
    (e: GestureResponderEvent) => {
      e.stopPropagation();
      onAddPress(item);
    },
    [onAddPress, item]
  );

  const handleRowPress = useCallback(() => {
    onRowPress(playerId, restProps.playerName);
  }, [onRowPress, playerId, restProps.playerName]);

  return (
    <SimilarPlayerRowView
      onAddPress={handleAddPress}
      onRowPress={handleRowPress}
      {...restProps}
    />
  );
};

SimilarPlayerRowContainer.displayName = "SimilarPlayerRowContainer";

export default memo(SimilarPlayerRowContainer);
